import { useContext, useState } from 'react';

import { MAIN_CONTENT_ID } from '@/components/accessibility/SkipToMain';
import AudioPlayer from '@/components/app/contentPlayer/AudioPlayer';
import { MediaPlayerContext } from '@/components/app/contentPlayer/MediaPlayerProvider';
import VideoPlayer from '@/components/app/contentPlayer/VideoPlayer';
import AppBackground from '@/components/app/layout/AppBackground';
import DiscountModal from '@/components/app/layout/DiscountModal';
import Header from '@/components/app/layout/HeaderRefresh';
import { usePacksContext } from '@/components/app/layout/PacksProvider';
import { ShareContext } from '@/components/app/shareFreeContent/ShareProvider';
import Sidebar from '@/components/app/sidebar/Sidebar';
import ControlFlow from '@/components/SimplifiedSignup/configs/ControlModal/Flow';
import {
	SimplifiedSignupContextProvider,
	useSimplifiedSignupContext,
} from '@/components/SimplifiedSignup/Context';
import { ActiveScreenConfig } from '@/components/SimplifiedSignup/Context/types';
import SimplifiedSignupModal from '@/components/SimplifiedSignup/SimplifiedSignupModal';
import UpsellFooter from '@/components/UpsellFooter';
import { useSimplifiedSignupModalContext } from '@/context/SimplifiedSignupModalContextProvider';
import { useAppLoadedState, useThemeState, useUserState } from '@/hooks/store';
import { useMaxWidth } from '@/hooks/ui/useMaxWidth';
import { useAmplitudeExperiment } from '@/hooks/utils/experiments/useAmplitudeExperiment';
import { calmLogger } from '@/utils/calmLogger';
import { AmplitudeExperimentNames } from '@/utils/experiments/amplitudeExperiment';

import { CookiePreferencesBannerContext } from '../CookiePreferencesBannerProvider';
import { OfferBannerContext } from '../OfferBannerProvider';
import ShareContentBanner from '../ShareContentBanner';
import ShareContentModal from '../ShareContentModal';
import { menuItems } from './data';
import { Container, Main, Wrapper } from './styles';
import { AppLayoutProps } from './types';

const AppLayout = ({ children, appHeaderProps, variant }: AppLayoutProps) => {
	const appLoaded = useAppLoadedState();
	const theme = useThemeState();
	// App-router routes pass `variant` explicitly (their persistent store has no
	// `theme.webAppLayout` set, since that Redux flag is what this prop replaces there).
	const activeVariant = variant ?? theme.webAppLayout;
	const { onShowSharePayment, sidebarCta, isShareContentRecipient } = useContext(ShareContext);
	const { onPause, contentType, isZenMode, playerMode } = useContext(MediaPlayerContext);
	const { fixedBottomHeight, isOpen: isCookieBannerOpen } = useContext(CookiePreferencesBannerContext);
	const { isBannerVisible } = useContext(OfferBannerContext);
	const { flowConfig = ControlFlow } = useSimplifiedSignupContext();
	const { isModalOpen } = useSimplifiedSignupModalContext();
	// The in-app upsell modal is client-only (opens on user action, well after
	// `appLoaded`), so — unlike the SSR-plumbed offer pages — its enrollment is resolved from
	// AppLayout's own client experiment read, which has long since resolved by the time a modal
	// opens (AppLayout mounts globally at app load). Passed into the provider below so the modal's
	// payment form latches CS at first visible paint (its own experiment read is still `isLoading`
	// on a fresh mount, so the latch reads this SSR-prop slot). The `!isLoading` guard
	// falls back to legacy on the rare open before AppLayout's read resolves — never wrongly CS.
	const { isEnrolled: isCheckoutSessionsEnrolled, isLoading: isCheckoutSessionsLoading } =
		useAmplitudeExperiment(AmplitudeExperimentNames.CHECKOUT_SESSIONS);

	const [isNavOpen, setIsNavOpen] = useState(false);

	const user = useUserState();
	const [isTabletOrNarrower] = useMaxWidth('tablet');
	const { currentProgram, currentGuide } = usePacksContext();
	// UpsellFooter and the mini AudioPlayer/VideoPlayer are the only elements that sit
	// fixed at the bottom of the viewport; Wrapper only needs to reserve clearance for
	// them when one is actually going to render (mirrors UpsellFooter's own hide logic,
	// and AudioPlayer/VideoPlayer's own null-return conditions — a timer program or a
	// guide with no video asset renders neither player despite `contentType` being set).
	const hasUpsellFooter = !user && !isTabletOrNarrower;
	const hasMiniPlayer =
		(contentType === 'audio' && currentProgram?.meditation_type !== 'timer') ||
		(contentType === 'video' && Boolean(currentGuide?.asset?.url));
	const hasBottomOverlay = hasUpsellFooter || hasMiniPlayer;

	const onShareBannerCtaPress = () => {
		if (playerMode === 'playing') {
			onPause();
		}
		onShowSharePayment('banner').catch(error =>
			calmLogger.error('Error in AppLayout onShowSharePayment', {}, error),
		);
	};

	if (!activeVariant) return <>{children}</>;

	return (
		<>
			<AppBackground isZenMode={isZenMode} />
			<Wrapper
				$isZenMode={isZenMode}
				$isLargePlayer={activeVariant === 'player'}
				$isBannerVisible={isBannerVisible}
				$hasBottomOverlay={hasBottomOverlay}
			>
				<Sidebar
					menuItems={menuItems}
					upsellButtonCopy={isShareContentRecipient ? sidebarCta : undefined}
					onUpsellPress={isShareContentRecipient ? () => onShowSharePayment('sidebar') : undefined}
					isUpsellButtonVisible={isShareContentRecipient}
					bottomOffset={isCookieBannerOpen ? fixedBottomHeight : 0}
				/>

				<Main id={MAIN_CONTENT_ID}>
					<SimplifiedSignupContextProvider
						flowConfig={flowConfig}
						isCheckoutSessionsEnabled={!isCheckoutSessionsLoading && Boolean(isCheckoutSessionsEnrolled)}
					>
						<Header isNavOpen={isNavOpen} setIsNavOpen={setIsNavOpen} appHeaderProps={appHeaderProps} />

						<Container
							$isVisible={appLoaded}
							$hasBanner={isShareContentRecipient}
							$bottomOffset={isCookieBannerOpen ? fixedBottomHeight : 0}
							$isLargePlayer={activeVariant === 'player'}
							$hasBottomOverlay={hasBottomOverlay}
						>
							<ShareContentBanner
								showShareContent={isShareContentRecipient}
								onPress={onShareBannerCtaPress}
							/>
							<ShareContentModal />

							{children}
						</Container>
						{isModalOpen && (
							<SimplifiedSignupModal
								handleModalClose={async (activeScreenConfig: ActiveScreenConfig) => {
									if (activeScreenConfig.name === 'handoff') {
										await Promise.all([location.reload()]);
									}
								}}
							/>
						)}
					</SimplifiedSignupContextProvider>
				</Main>
				{contentType === 'video' && <VideoPlayer />}
				{contentType === 'audio' && <AudioPlayer />}
				<UpsellFooter />
			</Wrapper>
			<DiscountModal />
		</>
	);
};

export default AppLayout;
